import React, { FC, useRef } from 'react'

import { Reference, useMutation } from '@apollo/client'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { FormProvider, useForm } from 'react-hook-form'

import { CREATE_CUSTOMER_ADDRESS } from 'Const'
import { Customer, CustomerAddress } from 'Types'
import { noty } from 'Utils'

import AddressFields, { FormAddress } from 'Components/AddressFields'

interface Props {
  closeModal(): void
  customer: Pick<Customer, 'firstName' | 'lastName'>
  customerId: string
  googlePlacesApiKey: string
}

interface FormState {
  address: FormAddress
}

const noop = (): void => {}

const AddAddressModal: FC<Props> = ({ closeModal, customer, customerId, googlePlacesApiKey }) => {
  const form = useForm<FormState>()
  const { handleSubmit } = form
  const contentRef = useRef<HTMLDivElement>(null)
  // antd's Selects portal their dropdown to document.body by default, which
  // puts it outside Dialog.Content's DOM subtree — Radix then treats a click
  // on it as an outside click and swallows it before the option's onSelect
  // fires. Keeping the dropdown nested inside Content fixes that.
  const getPopupContainer = () => contentRef.current ?? document.body

  const [createAddress, { loading }] = useMutation(CREATE_CUSTOMER_ADDRESS, {
    update: (cache, { data: mutationData }) => {
      const newAddress = mutationData?.createCustomerAddress?.customerAddress as CustomerAddress | undefined
      if (!newAddress) return

      cache.modify({
        id: cache.identify({ __typename: 'Customer', id: customerId }),
        fields: {
          addresses: (existing: readonly Reference[] = [], { toReference }) => [
            ...existing,
            toReference({ __typename: 'CustomerAddress', id: newAddress.id })!,
          ],
        },
      })
    },
  })

  const onSubmit = handleSubmit(async ({ address }) => {
    try {
      await createAddress({
        variables: {
          input: {
            firstName: address.firstName,
            lastName: address.lastName,
            address1: address.address1,
            city: address.city,
            cityGooglePlaceId: address.cityGooglePlaceId,
            countryIsoCode: address.countryIsoCode,
            stateIsoCode: address.stateIsoCode,
            zipCode: address.zipCode,
            latitude: address.latitude,
            longitude: address.longitude,
          },
        },
      })
      closeModal()
    } catch (err) {
      noty(err instanceof Error ? err.message : 'Unable to save this address', 'error')
    }
  })

  return (
    <Dialog.Root open onOpenChange={(next) => !next && closeModal()}>
      <Dialog.Portal>
        <Dialog.Overlay className="user-profile-address-dialog__overlay" />
        <Dialog.Content ref={contentRef} className="user-profile-address-dialog__content">
          <FormProvider {...form}>
            <form onSubmit={(event) => void onSubmit(event)}>
              <div className="user-profile-address-dialog__header">
                <Dialog.Title className="user-profile-address-dialog__title">Add New Address</Dialog.Title>
                <Dialog.Description className="sr-only">Form to add a new delivery address</Dialog.Description>
                <Dialog.Close asChild>
                  <button aria-label="Close" className="user-profile-address-dialog__close" type="button">
                    <X aria-hidden size={18} strokeWidth={1.75} />
                  </button>
                </Dialog.Close>
              </div>

              <div className="user-profile-address-dialog__body">
                <AddressFields
                  calcTransportCost={noop}
                  cssPrefix="add-address"
                  getPopupContainer={getPopupContainer}
                  googlePlacesApiKey={googlePlacesApiKey}
                  nameGroupPrefix="address"
                  prefillName={customer}
                />
              </div>

              <div className="user-profile-address-dialog__footer">
                <Dialog.Close asChild>
                  <button className="user-profile-link-button" type="button">
                    Cancel
                  </button>
                </Dialog.Close>
                <button className="btn btn-accent" disabled={loading} type="submit">
                  {loading ? 'Saving…' : 'Save Address'}
                </button>
              </div>
            </form>
          </FormProvider>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default AddAddressModal
